import { deriveTheme } from '@ego/domain';

/**
 * Device-local custom themes, persisted as a JSON array under
 * `ego-custom-themes` (same key as iOS). Each entry stores the three seed
 * colors (the source of truth) plus a derived `tokens` snapshot — the
 * snapshot exists ONLY so the index.html flash script can apply the theme
 * before the derivation code loads.
 */
const STORAGE_KEY = 'ego-custom-themes';

function isValidEntry(entry) {
  return (
    entry &&
    typeof entry.id === 'string' &&
    typeof entry.name === 'string' &&
    typeof entry.bgHex === 'string' &&
    typeof entry.accentHex === 'string' &&
    typeof entry.textHex === 'string'
  );
}

function readAll() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(parsed) ? parsed.filter(isValidEntry) : [];
  } catch {
    return [];
  }
}

function persist(themes) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(themes));
}

/** All saved custom themes (corrupt or missing storage → empty array). */
export function listCustomThemes() {
  return readAll();
}

/**
 * Inserts (no `id`) or updates (existing `id`) a custom theme, recomputing
 * the derived `tokens` snapshot from the seeds. Returns the saved entry.
 */
export function saveCustomTheme({ id, name, bgHex, accentHex, textHex }) {
  const themes = readAll();
  const entry = {
    id: id || crypto.randomUUID(),
    name,
    bgHex,
    accentHex,
    textHex,
    tokens: deriveTheme(name, bgHex, accentHex, textHex),
  };
  const index = themes.findIndex((t) => t.id === entry.id);
  if (index >= 0) themes[index] = entry;
  else themes.push(entry);
  persist(themes);
  return entry;
}

/** Removes a saved custom theme by id. */
export function deleteCustomTheme(id) {
  persist(readAll().filter((t) => t.id !== id));
}

/**
 * Full replace of the stored list (used by cross-device sync pulls),
 * regenerating each entry's `tokens` snapshot so the flash script stays
 * correct. Invalid entries are dropped.
 */
export function replaceCustomThemes(entries) {
  const themes = (Array.isArray(entries) ? entries : [])
    .filter(isValidEntry)
    .map(({ id, name, bgHex, accentHex, textHex }) => ({
      id,
      name,
      bgHex,
      accentHex,
      textHex,
      tokens: deriveTheme(name, bgHex, accentHex, textHex),
    }));
  persist(themes);
}
